import React, {useState, useEffect} from 'react';
import {StyleSheet, ScrollView, RefreshControl} from 'react-native';
import {Box, HStack, Heading, Pressable, VStack} from 'native-base';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import * as Keychain from '../../../../utils/keychain';
import {
  BalanceSummaryIcon,
  FilterCircleFilledIcon,
  FilterCircleIcon,
  RemoveFilterCircleIcon,
} from './../../../../components/icons';
import SemaphoreItem from './SemaphoreItem';
import DashboardTemplate from '../../../../components/templates/dashboard';
import {useContextState as userContext} from '../../../../context/user';
import useToast from '../../../../hooks';
import {ISemaphoreValue} from '../../../../interfaces/dtos';
import {SemaphoreKeys} from '../../../../constants/enumerations';
import {
  DashboardItem,
  SummaryAccountItem,
} from '../../../../components/dashboard';
import {StackParamList} from '../../../../navigators';
import {SpinnerModal} from '../../../../components/modals';
import {getSemaphoreColor} from '../../../../constants/semaphore';
import {message} from '../../../../utils/messages';
import useClientContext from '../../../../context/client/Context';
import {IPortfolioFilter} from '../../../../context/commons/interfaces';

type ScreenProps = NativeStackScreenProps<StackParamList, 'DashboardClient'>;

interface IFilterIcon {
  clientPortfolioFilter: IPortfolioFilter | null;
}

const FilterIcon = ({clientPortfolioFilter}: IFilterIcon) => {
  if (clientPortfolioFilter) {
    return <FilterCircleFilledIcon width={30} style={styles.balanceIcon} />;
  }
  return <FilterCircleIcon width={30} style={styles.balanceIcon} />;
};

export const DashboardClientScreen = ({navigation}: ScreenProps) => {
  const {signOff, notifications, fetchNotificationList, profile} =
    userContext();

  const {
    fetchBalanceSheet,
    balanceSheet,
    fetchSemaphoreItems,
    semaphoreItems,
    clientPortfolioFilter,
    setClientPortfolioFilter,
    fetchResources,
  } = useClientContext();

  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const {showServerError} = useToast();

  const getNotifications = async () => {
    await fetchNotificationList();
  };

  const getIndicators = async () => {
    if (await fetchBalanceSheet(clientPortfolioFilter)) {
      if (await fetchSemaphoreItems(clientPortfolioFilter)) {
        return;
      }
    }
    throw new Error('Internal error');
  };

  const getData = async () => {
    try {
      setIsLoading(true);
      await fetchResources();
      await getNotifications();
      await getIndicators();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  const refreshData = async () => {
    await getData();
    setRefreshing(false);
  };

  const getDataIndicators = async () => {
    try {
      setIsLoading(true);
      await getIndicators();
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      showServerError();
    }
  };

  useEffect(() => {
    getDataIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientPortfolioFilter]);

  useEffect(() => {
    if (balanceSheet) {
      return;
    }
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceSheet]);

  useEffect(() => {
    if (refreshing) {
      refreshData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshing]);

  const handleTapNotifications = () => {
    navigation.navigate('Notifications');
  };

  const handleShowProfile = () => {
    navigation.navigate('Profile');
  };

  const handleLogOut = () => {
    signOff();
    Keychain.getGenericPassword();
  };

  const getCompanyName = () => {
    if (profile && profile.company) {
      return profile.company;
    }
    return '';
  };

  const getUserName = () => {
    if (profile && profile.firstName) {
      return `${profile?.firstName}`;
    }
    return '';
  };

  const handleTapProdile = () => {
    handleShowProfile();
  };

  const handleOnPress = (position: number) => {
    if (semaphoreItems) {
      const item = semaphoreItems[position];
      if (item) {
        navigation.navigate('Debtors', {position});
      }
    }
  };

  const handleOnRefresh = async () => {
    setRefreshing(true);
  };

  const getSemaphoreItems = (): ISemaphoreValue[] => {
    if (semaphoreItems && !isLoading) {
      return semaphoreItems;
    }

    return [
      {
        order: 0,
        key: SemaphoreKeys.Red,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Orange,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Yellow,
        value: 0,
        amount: '---',
      },
      {
        order: 0,
        key: SemaphoreKeys.Green,
        value: 0,
        amount: '---',
      },
    ];
  };

  const handleOnFilterPress = () => {
    navigation.navigate('PortfolioFilter');
  };

  const handleOnClearFilterPress = () => {
    setClientPortfolioFilter(null);
  };

  const handleGoBack = () => {
    navigation.navigate('HomeClient');
  };

  return (
    <DashboardTemplate
      title={getUserName()}
      summary={getCompanyName()}
      notifications={notifications ? notifications : []}
      onTapNotifications={() => handleTapNotifications()}
      onTapLogout={() => handleLogOut()}
      onTapProfile={() => handleTapProdile()}
      backButton={{
        onPress: () => handleGoBack(),
        label: 'back-dashboard',
      }}>
      <ScrollView
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleOnRefresh}
            tintColor="transparent"
          />
        }>
        <SpinnerModal
          show={isLoading}
          title={
            balanceSheet && semaphoreItems ? message('updating') : undefined
          }
        />
        {
          <Box borderRadius="md" marginTop={22}>
            <VStack space={3} justifyContent="center" marginX={4}>
              <DashboardItem
                label="dashboard-summary-account-title"
                icon={
                  <BalanceSummaryIcon width={40} style={styles.balanceIcon} />
                }
                shadow={true}>
                <SummaryAccountItem
                  localBalance={!isLoading ? balanceSheet?.local : '---'}
                  foreignBalance={!isLoading ? balanceSheet?.foreign : '---'}
                />
              </DashboardItem>
              <HStack justifyContent={'space-between'} alignItems={'center'}>
                <Heading fontSize={16} marginTop={0} color="#06355E">
                  {message('Accounts receivable semaphore title')}
                </Heading>
                <HStack paddingBottom={2} paddingRight={3} space={2}>
                  {clientPortfolioFilter && (
                    <Pressable onPress={() => handleOnClearFilterPress()}>
                      <RemoveFilterCircleIcon
                        width={30}
                        style={styles.balanceIcon}
                      />
                    </Pressable>
                  )}
                  <Pressable onPress={() => handleOnFilterPress()}>
                    <FilterIcon clientPortfolioFilter={clientPortfolioFilter} />
                  </Pressable>
                </HStack>
              </HStack>
              {getSemaphoreItems()?.map(
                (item: ISemaphoreValue, position: number) => {
                  // Destructure the properties you need, excluding 'key' if it exists in ISemaphoreValue
                  const {key, value, amount, order, ...restOfItem} = item; // Assuming ISemaphoreValue might have 'key'

                  return (
                    <DashboardItem key={`dashboard-${key}`}>
                      {' '}
                      {/* Use item.key or the destructured key here */}
                      <SemaphoreItem
                        // Spread the rest of the item properties and add/override others
                        {...restOfItem}
                        key={key} // Pass the key directly if SemaphoreItem needs it as a prop (unlikely for list rendering, but possible)
                        value={value}
                        amount={amount}
                        order={order}
                        label={`label-${key}`}
                        color={getSemaphoreColor(key)}
                        position={position}
                        onPress={handleOnPress}
                      />
                    </DashboardItem>
                  );
                },
              )}
            </VStack>
          </Box>
        }
      </ScrollView>
    </DashboardTemplate>
  );
};

const styles = StyleSheet.create({
  balanceIcon: {
    marginTop: 10,
  },
});
